import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertOctagon, AlertTriangle, ArrowDown, ArrowRight, ArrowUp, BadgeIndianRupee, CheckCircle2, Info, Layers, Package,
  Percent, Scale, Sparkles, TrendingDown, Users,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, formatApiError, formatDate } from "@/lib/api";
import Collapse, { CARD } from "@/components/Collapse";
import { usePeriod, RollingPeriod, FinancialYearBar } from "@/components/PeriodControls";

// Checked for colour-blind separation: blue is revenue, orange is orders received. Gains and losses are
// always shown with a sign and a word as well as a colour.
const BLUE = "#2563EB";
const ORANGE = "#EA580C";
const LEVEL = {
  red:   { box: "border-red-200 bg-red-50",         text: "text-red-800",     icon: AlertOctagon,  label: "Act" },
  amber: { box: "border-amber-200 bg-amber-50",     text: "text-amber-900",   icon: AlertTriangle, label: "Watch" },
  good:  { box: "border-emerald-200 bg-emerald-50", text: "text-emerald-800", icon: CheckCircle2,  label: "Good" },
};
const CAUSE = { price: "Priced low", material: "Material cost", waste: "Wasted material" };
const CAUSE_COLOR = { price: "bg-blue-500", material: "bg-slate-500", waste: "bg-orange-500" };

const n0 = (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN"));
// Rupees in lakh / crore, the way the business talks about them.
const rs = (v) => {
  if (v == null) return "-";
  const a = Math.abs(v), sign = v < 0 ? "-" : "";
  if (a >= 1e7) return `${sign}₹ ${(a / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `${sign}₹ ${(a / 1e5).toFixed(1)} L`;
  return `${sign}₹ ${Math.round(a).toLocaleString("en-IN")}`;
};
const monthLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
const pctChange = (cur, prev) => (prev ? Math.round(((cur - prev) / prev) * 1000) / 10 : null);
const tip = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 };

function Change({ cur, prev, better = "up", pts = false }) {
  const d = pts ? (cur != null && prev != null ? Math.round((cur - prev) * 10) / 10 : null) : pctChange(cur, prev);
  if (d == null || d === 0) return null;
  const good = better === "up" ? d > 0 : d < 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${good ? "text-emerald-600" : "text-red-600"}`}>
      {d > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}{Math.abs(d)}{pts ? " pts" : "%"}
    </span>
  );
}

function Kpi({ label, value, sub, delta, tone = "neutral" }) {
  const ring = tone === "bad" ? "ring-red-300/70" : tone === "warn" ? "ring-amber-300/70" : "ring-white/10";
  return (
    <div className={`rounded-xl bg-white/5 p-4 ring-1 backdrop-blur ${ring}`}>
      <div className="text-[11px] uppercase tracking-wider text-slate-300 font-medium">{label}</div>
      <div className="mt-2 flex items-end gap-2">
        <span className="font-mono-num text-3xl font-bold leading-none">{value}</span>
        {delta}
      </div>
      <div className="mt-1.5 text-[11px] text-slate-400">{sub}</div>
    </div>
  );
}

function Empty({ icon: Icon = Info, children }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
      <Icon size={18} className="mt-0.5 shrink-0 text-slate-400" /> <div>{children}</div>
    </div>
  );
}

function Th({ children, right }) {
  return <th className={`py-2 pr-3 font-medium ${right ? "text-right" : ""}`}>{children}</th>;
}

export default function SalesDashboard() {
  const period = usePeriod();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data: d } = await api.get("/sales-dashboard", { params: period.params });
      setData(d);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [period.days, period.fy, period.month]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="p-8" data-testid="sales-error">
        <div className="mb-3 text-sm text-red-700">{err}</div>
        <button onClick={load} className="rounded-full bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white">Retry</button>
      </div>
    );
  }
  if (!data) return <div className="p-8 label-caps">Loading…</div>;

  const { kpi, bridge, margin, waste, gaps, concentration: conc } = data;
  const rev = kpi.revenue;
  const rch = pctChange(rev.cur, rev.prev);
  const hasRev = rev.cur > 0 || rev.prev > 0;
  const bridgeRows = [
    { key: "prev", label: `Revenue, ${data.period.prev_label}`, value: bridge.prev, kind: "total" },
    { key: "new", label: `New customers (${bridge.counts.new})`, value: bridge.new },
    { key: "grew", label: `Customers who bought more (${bridge.counts.grew})`, value: bridge.grew },
    { key: "shrank", label: `Customers who bought less (${bridge.counts.shrank})`, value: bridge.shrank },
    { key: "stopped", label: `Customers who stopped (${bridge.counts.stopped})`, value: bridge.stopped },
    { key: "cur", label: `Revenue, ${data.period.label}`, value: bridge.cur, kind: "total" },
  ];
  const bridgeMax = Math.max(1, ...bridgeRows.map((r) => Math.abs(r.value)));
  const hasMonthly = data.monthly.some((m) => m.revenue || m.intake);
  const biggestOrder = waste.top_orders[0];
  const warnings = [
    gaps.implausible_prices > 0 && `${gaps.implausible_prices} jobs have a unit price above ₹100 a label (likely a per-thousand price or a typo). Their value is left out of "orders received" and the open-order value.`,
    gaps.suspect_margins > 0 && `${gaps.suspect_margins} orders have material cost far above the invoice linked to them (likely a partial invoice), so they are left out of margin figures.`,
    gaps.suspect_yields > 0 && `${gaps.suspect_yields} orders show a material yield too low to be believable (often only part of the order was forwarded), so they are left out of the wastage figures.`,
    gaps.orders_undated > 0 && `${gaps.orders_undated} orders with a material cost have no date and cannot be placed in a period.`,
    gaps.low_data && `Only ${gaps.orders_in_period} orders in this period have a material-cost record, so margin and wastage figures here are a small sample.`,
  ].filter(Boolean);

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="sales-page">
      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#101a2e] to-[#1a2a47] text-white">
        <div className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-[#FF5A00]/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative px-8 pt-7 pb-6">
          <div className="flex flex-wrap items-start gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#FF8A4C] font-semibold">Revenue and profit</div>
              <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight mt-1">Sales Dashboard</h1>
              <p className="mt-2 text-sm text-slate-300 max-w-3xl" data-testid="sales-headline">
                {hasRev ? <>
                  <b className="text-white">{rs(rev.cur)}</b> invoiced in {data.period.label}
                  {rch != null && <> (<b className={rch < 0 ? "text-rose-300" : "text-emerald-300"}>{rch > 0 ? "up" : "down"} {Math.abs(rch)}%</b> on {data.period.prev_label})</>}
                  {margin.cur_pct != null && <><span className="mx-2 text-slate-500">·</span><b className="text-white">{margin.cur_pct}%</b> material margin</>}
                  {margin.gap_total > 0 && <><span className="mx-2 text-slate-500">·</span><b className="text-orange-300">{rs(margin.gap_total)}</b> of margin lost on weak orders</>}
                </> : <>No invoices in {data.period.label}</>}
              </p>
            </div>
            <div className="ml-auto flex flex-col items-end gap-2">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Data as of</div>
                <div className="font-display font-bold text-xl leading-tight">{formatDate(data.as_of)}</div>
              </div>
              <RollingPeriod period={period} />
              {data.using_latest_data && <span className="text-[11px] text-sky-200">Your data ends before today, so this shows the latest data.</span>}
            </div>
          </div>

          <div className="mt-5">
            <FinancialYearBar period={period} data={data} note="Open orders always show today's position." />
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Kpi label="Revenue" value={rs(rev.cur)} delta={<Change cur={rev.cur} prev={rev.prev} />}
                 sub={`${n0(kpi.invoices.cur)} invoices`} tone={rch != null && rch <= -10 ? "bad" : "neutral"} />
            <Kpi label="Orders received" value={rs(kpi.intake.cur)} delta={<Change cur={kpi.intake.cur} prev={kpi.intake.prev} />} sub="value of POs booked" />
            <Kpi label="Material margin" value={margin.cur_pct != null ? `${margin.cur_pct}%` : "-"}
                 delta={<Change cur={margin.cur_pct} prev={margin.prev_pct} pts />} sub={`${margin.orders} orders costed`}
                 tone={margin.cur_pct != null && margin.prev_pct != null && margin.cur_pct < margin.prev_pct - 5 ? "bad" : "neutral"} />
            <Kpi label="Customers billed" value={n0(kpi.customers.cur)} delta={<Change cur={kpi.customers.cur} prev={kpi.customers.prev} />}
                 sub={`top one is ${conc.top1}% of revenue`} tone={conc.top1 >= 25 ? "warn" : "neutral"} />
            <Kpi label="Open orders" value={rs(kpi.backlog.value)} sub={`${n0(kpi.backlog.jobs)} jobs not yet dispatched`} />
            <Kpi label="Late open orders" value={rs(kpi.backlog.overdue_value)} sub={`${n0(kpi.backlog.overdue_jobs)} jobs past due`}
                 tone={kpi.backlog.overdue_value > 0 ? "warn" : "neutral"} />
          </div>
        </div>
      </section>

      <div className="px-8 py-6 space-y-6">
        {/* ===== Insights ===== */}
        <Collapse id="sales-insights" testId="sales-insights" defaultOpen title="What the numbers say"
                  icon={<Sparkles size={18} className="text-[#FF5A00]" />}
                  summary={data.insights.length ? `${data.insights.length} findings` : "Nothing notable"}>
          {data.insights.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={18} /> Revenue, margin and material use are all within their normal range for this period.</div>
          ) : (
            <div className="grid gap-2">
              {data.insights.map((i, n) => {
                const L = LEVEL[i.level], Icon = L.icon;
                const body = (
                  <div className={`flex items-start gap-3 rounded-lg border p-3 ${L.box}`}>
                    <Icon size={18} className={`mt-0.5 shrink-0 ${L.text}`} />
                    <span className={`w-12 shrink-0 pt-0.5 text-[10px] font-bold uppercase tracking-wide ${L.text}`}>{L.label}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900">{i.title}</div>
                      <div className="text-xs text-slate-600">{i.detail}</div>
                    </div>
                    {i.impact != null && <span className={`shrink-0 font-mono-num text-sm font-bold ${i.impact < 0 ? "text-red-700" : "text-emerald-700"}`}>{i.impact > 0 ? "+" : ""}{rs(i.impact)}</span>}
                    {i.to && <ArrowRight size={15} className="mt-0.5 shrink-0 text-slate-400" />}
                  </div>
                );
                return i.to ? <Link key={n} to={i.to}>{body}</Link> : <div key={n}>{body}</div>;
              })}
            </div>
          )}
        </Collapse>

        {/* ===== AI advisor ===== */}
        <Link to={`/advisor?q=${encodeURIComponent("Why did revenue change this period, and what should I do about it?")}`}
              className={`${CARD} flex items-center gap-3 p-4 transition hover:shadow-md`} data-testid="sales-ask-link">
          <Sparkles size={18} className="text-violet-600" />
          <div>
            <div className="font-display text-lg font-bold text-slate-900">Ask the AI advisor about this</div>
            <div className="text-xs text-slate-500">It reads the sales, production and delivery dashboards and answers as a short note, a table or a chart. You see the cost first, and nothing is sent until you approve it.</div>
          </div>
          <ArrowRight size={16} className="ml-auto shrink-0 text-slate-400" />
        </Link>

        {/* ===== Where revenue is hit ===== */}
        <Collapse id="sales-bridge" testId="sales-bridge" defaultOpen title="Where revenue came from, and where it was hit"
                  icon={<TrendingDown size={18} className="text-[#FF5A00]" />}
                  summary={hasRev ? `${rs(bridge.prev)} to ${rs(bridge.cur)}` : "No invoices"}>
          {!hasRev ? <Empty icon={TrendingDown}>No invoices in this period or the one before it.</Empty> : (
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <div className="mb-3 text-sm font-semibold text-slate-900">From {data.period.prev_label} to {data.period.label}</div>
                <div className="grid gap-2.5">
                  {bridgeRows.map((r) => {
                    const total = r.kind === "total";
                    const color = total ? "bg-slate-800" : r.value >= 0 ? "bg-emerald-500" : "bg-red-500";
                    return (
                      <div key={r.key} className="grid grid-cols-[minmax(0,1fr)_7rem] items-center gap-3">
                        <div>
                          <div className={`text-xs ${total ? "font-bold text-slate-900" : "text-slate-600"}`}>{r.label}</div>
                          <div className="mt-1 h-2 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${color}`} style={{ width: `${Math.max(1, (100 * Math.abs(r.value)) / bridgeMax)}%` }} /></div>
                        </div>
                        <div className={`text-right font-mono-num text-sm font-bold ${total ? "text-slate-900" : r.value >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                          {!total && r.value > 0 ? "+" : ""}{rs(r.value)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid gap-4 content-start">
                <div>
                  <div className="mb-2 text-sm font-semibold text-slate-900">Biggest drops</div>
                  {data.customers.decliners.length === 0 ? <p className="text-xs text-slate-500">No customer bought less.</p> : data.customers.decliners.map((c) => (
                    <div key={c.name} className="flex items-baseline justify-between border-t border-slate-100 py-1.5 text-sm">
                      <span className="truncate pr-3 text-slate-800">{c.name}{c.stopped && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">Stopped</span>}</span>
                      <span className="shrink-0 font-mono-num font-semibold text-red-700">{rs(c.change)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="mb-2 text-sm font-semibold text-slate-900">Biggest gains</div>
                  {data.customers.growers.length === 0 ? <p className="text-xs text-slate-500">No customer bought more.</p> : data.customers.growers.map((c) => (
                    <div key={c.name} className="flex items-baseline justify-between border-t border-slate-100 py-1.5 text-sm">
                      <span className="truncate pr-3 text-slate-800">{c.name}{c.new && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">New</span>}</span>
                      <span className="shrink-0 font-mono-num font-semibold text-emerald-700">+{rs(c.change)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Collapse>

        {/* ===== Orders earning less than expected ===== */}
        <Collapse id="sales-margin" testId="sales-margin" defaultOpen title="Orders that earned less than expected"
                  icon={<Scale size={18} className="text-[#FF5A00]" />}
                  summary={margin.below_count ? `${margin.below_count} orders · ${rs(margin.gap_total)} of margin lost` : "None below expected"}>
          {margin.orders === 0 ? (
            <Empty icon={Scale}>No order in this period has both an invoice and a material-cost record, so profit cannot be worked out yet. Material margin is revenue less raw-material cost; labour and overheads are not included.</Empty>
          ) : margin.low_orders.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={16} /> Every costed order earned close to what that product normally earns.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <Th>Order</Th><Th right>Revenue</Th><Th right>Margin</Th><Th right>Expected</Th><Th right>Lost</Th><Th>Why</Th>
                  </tr></thead>
                  <tbody>
                    {margin.low_orders.map((o) => {
                      const total = o.split.price + o.split.material + o.split.waste;
                      return (
                        <tr key={o.mpwo} className="border-t border-slate-100 align-top" data-testid="sales-low-order">
                          <td className="py-2.5 pr-3"><div className="font-mono-num font-semibold text-slate-900">{o.mpwo}</div><div className="max-w-[16rem] truncate text-xs text-slate-500">{o.customer}</div></td>
                          <td className="py-2.5 pr-3 text-right font-mono-num">{rs(o.revenue)}</td>
                          <td className={`py-2.5 pr-3 text-right font-mono-num font-semibold ${o.margin_pct < 0 ? "text-red-700" : "text-slate-900"}`}>{o.margin_pct}%</td>
                          <td className="py-2.5 pr-3 text-right font-mono-num text-slate-500">{o.expected_margin_pct}%</td>
                          <td className="py-2.5 pr-3 text-right font-mono-num font-bold text-red-700">{rs(o.gap)}</td>
                          <td className="py-2.5 text-xs">
                            {total > 0 && (
                              <>
                                <div className="flex h-1.5 w-40 overflow-hidden rounded-full bg-slate-100 gap-px">
                                  {["price", "material", "waste"].map((k) => o.split[k] > 0 && <div key={k} className={CAUSE_COLOR[k]} style={{ width: `${(100 * o.split[k]) / total}%` }} title={`${CAUSE[k]} ${rs(o.split[k])}`} />)}
                                </div>
                                <div className="mt-1 text-slate-600">
                                  {["price", "material", "waste"].filter((k) => o.split[k] > 0).map((k) => `${CAUSE[k]} ${rs(o.split[k])}`).join(" · ")}
                                </div>
                              </>
                            )}
                            {o.tags.map((t) => <div key={t} className="mt-0.5 text-slate-500">{t}</div>)}
                            {total === 0 && o.tags.length === 0 && <span className="text-slate-400">No earlier orders of this product to compare with</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Material margin is revenue less raw-material cost; labour and overheads are not included. "Expected" is what the same product earned on its earlier orders (or the portfolio median where there are fewer than 3).
                The split into price, material cost and wasted material only works for products with 3 or more earlier orders.
                {margin.explained < margin.gap_total && ` Of the ${rs(margin.gap_total)} lost, ${rs(margin.explained)} could be traced to a cause.`}
              </p>
            </>
          )}
        </Collapse>

        {/* ===== Wastage ===== */}
        <Collapse id="sales-waste" testId="sales-waste" defaultOpen title="Where material was wasted"
                  icon={<Layers size={18} className="text-[#FF5A00]" />}
                  summary={waste.total_excess_cost ? `${rs(waste.total_excess_cost)} of extra material · ${n0(waste.total_excess_m)} m` : "No extra material found"}>
          {waste.orders === 0 ? (
            <Empty icon={Layers}>No order in this period has the material records needed to measure yield (metres used and labels forwarded).</Empty>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Extra material used</div>
                  <div className="mt-1 font-mono-num text-2xl font-bold text-slate-900">{rs(waste.total_excess_cost)}</div>
                  <div className="text-xs text-slate-500">{n0(waste.total_excess_m)} m beyond a normal run, across {waste.orders} orders</div>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Worst single order</div>
                  <div className="mt-1 font-mono-num text-2xl font-bold text-slate-900">{biggestOrder ? rs(biggestOrder.excess_cost) : "-"}</div>
                  <div className="truncate text-xs text-slate-500">{biggestOrder ? `${biggestOrder.mpwo}, ${biggestOrder.customer}` : "none"}</div>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Typical material yield</div>
                  <div className="mt-1 font-mono-num text-2xl font-bold text-slate-900">{waste.median_yield_pct != null ? `${waste.median_yield_pct}%` : "-"}</div>
                  <div className="text-xs text-slate-500">of what the label geometry allows (all-order benchmark {waste.benchmark_pct}%)</div>
                </div>
              </div>

              <div className="mt-5 text-sm font-semibold text-slate-900">Orders that used the most extra material</div>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <Th>Order</Th><Th right>Metres used</Th><Th>Yield against a normal run</Th><Th right>Extra</Th><Th>Likely reasons</Th>
                  </tr></thead>
                  <tbody>
                    {waste.top_orders.map((o) => (
                      <tr key={o.mpwo} className="border-t border-slate-100 align-top" data-testid="sales-waste-order">
                        <td className="py-2.5 pr-3"><div className="font-mono-num font-semibold text-slate-900">{o.mpwo}</div><div className="max-w-[16rem] truncate text-xs text-slate-500">{o.customer}</div></td>
                        <td className="py-2.5 pr-3 text-right font-mono-num">{n0(o.cons_m)}</td>
                        <td className="py-2.5 pr-3">
                          <div className="flex items-center gap-2">
                            <div className="relative h-1.5 w-28 rounded-full bg-slate-100">
                              <div className="h-1.5 rounded-full bg-orange-500" style={{ width: `${o.yield_pct}%` }} />
                              <div className="absolute -top-0.5 h-2.5 w-px bg-slate-900" style={{ left: `${o.ref_yield_pct}%` }} title={`Normal for ${o.ref_basis === "product" ? "this product" : "all orders"}: ${o.ref_yield_pct}%`} />
                            </div>
                            <span className="font-mono-num text-xs">{o.yield_pct}% <span className="text-slate-400">vs {o.ref_yield_pct}%</span></span>
                          </div>
                          <div className="mt-0.5 text-[10px] text-slate-400">normal = {o.ref_basis === "product" ? "this product's earlier runs" : "all recorded orders"}</div>
                        </td>
                        <td className="py-2.5 pr-3 text-right"><div className="font-mono-num font-bold text-red-700">{rs(o.excess_cost)}</div><div className="font-mono-num text-xs text-slate-500">{n0(o.excess_m)} m</div></td>
                        <td className="py-2.5 text-xs text-slate-600">{o.tags.length ? o.tags.map((t) => <div key={t} className="mb-0.5">{t}</div>) : <span className="text-slate-400">Nothing in the order record explains it</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 text-sm font-semibold text-slate-900">What goes with high wastage <span className="font-normal text-slate-500">(based on {waste.drivers_scope})</span></div>
              {waste.drivers.length === 0 ? <p className="mt-2 text-xs text-slate-500">No kind of order yields clearly worse than the rest.</p> : (
                <div className="mt-2 grid gap-2.5">
                  {waste.drivers.map((d) => (
                    <div key={`${d.dimension}-${d.value}`} className="grid grid-cols-[12rem_minmax(0,1fr)_6rem] items-center gap-3 text-sm" data-testid="sales-driver">
                      <div className="truncate"><span className="text-[10px] uppercase tracking-wide text-slate-400">{d.dimension}</span><div className="truncate font-medium text-slate-800">{d.value}</div></div>
                      <div>
                        <div className="h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-orange-500" style={{ width: `${Math.min(100, d.gap_pts * 4)}%` }} /></div>
                        <div className="mt-0.5 text-[11px] text-slate-500">{d.orders} orders · yield {d.yield_pct}% · {d.gap_pts} points below the rest</div>
                      </div>
                      <div className="text-right font-mono-num font-bold text-red-700">{rs(d.excess_cost)}</div>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-4 text-xs text-slate-500">
                Yield is labels forwarded per metre used, divided by what the label size, gap and number across allow. Its level includes edge trim and gaps, so it is only meaningful against a benchmark.
                "Extra" is the metres beyond what a normal run of the same product (or, without history, of all orders) would have used, priced at that order's own material cost per metre.
                {waste.from_printing_log > 0 && ` ${waste.from_printing_log} orders also have real counters in the Printing Log.`}
              </p>
            </>
          )}
        </Collapse>

        {/* ===== Customers ===== */}
        <Collapse id="sales-customers" testId="sales-customers" defaultOpen title="Customers"
                  icon={<Users size={18} className="text-[#FF5A00]" />}
                  summary={data.customers.top[0] ? `Top: ${data.customers.top[0].name} (${data.customers.top[0].share}%)` : "No customers"}>
          {data.customers.top.length === 0 ? <Empty icon={Users}>No customer was invoiced in this period.</Empty> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <Th>Customer</Th><Th>Share of revenue</Th><Th right>Revenue</Th><Th right>Against last period</Th><Th right>Material margin</Th><Th right>Extra material</Th>
                </tr></thead>
                <tbody>
                  {data.customers.top.map((c) => (
                    <tr key={c.name} className="border-t border-slate-100">
                      <td className="max-w-[18rem] truncate py-2.5 pr-3 font-semibold text-slate-900">{c.name}</td>
                      <td className="py-2.5 pr-3"><div className="flex items-center gap-2"><div className="h-1.5 w-24 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, c.share * 2)}%`, background: BLUE }} /></div><span className="font-mono-num text-xs">{c.share}%</span></div></td>
                      <td className="py-2.5 pr-3 text-right font-mono-num font-semibold">{rs(c.revenue)}</td>
                      <td className={`py-2.5 pr-3 text-right font-mono-num text-xs font-semibold ${c.change < 0 ? "text-red-700" : c.change > 0 ? "text-emerald-700" : "text-slate-500"}`}>{c.change > 0 ? "+" : ""}{rs(c.change)}</td>
                      <td className="py-2.5 pr-3 text-right font-mono-num">{c.margin_pct != null ? `${c.margin_pct}%` : "-"}</td>
                      <td className="py-2.5 text-right font-mono-num text-xs">{c.excess_cost ? rs(c.excess_cost) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Collapse>

        {/* ===== Monthly ===== */}
        <Collapse id="sales-monthly" testId="sales-monthly" title="Revenue and orders received by month"
                  icon={<BadgeIndianRupee size={18} className="text-[#FF5A00]" />}
                  summary={data.kpi.best_month && data.kpi.best_month.revenue ? `Best month ${monthLabel(data.kpi.best_month.month)}: ${rs(data.kpi.best_month.revenue)}` : "No invoices"}>
          {!hasMonthly ? <Empty>No invoices or orders in these months.</Empty> : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.monthly} margin={{ left: 0, right: 8, top: 4 }}>
                  <CartesianGrid stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => (v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : v)} />
                  <Tooltip labelFormatter={monthLabel} formatter={(v, k) => [rs(v), k]} contentStyle={tip} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Invoiced" fill={BLUE} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="intake" name="Orders received" fill={ORANGE} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="mt-1 text-xs text-slate-500">When orders received stay level but invoiced falls, orders are waiting to be delivered. When orders received fall first, fewer POs are coming in.</p>
            </>
          )}
        </Collapse>

        {/* ===== Products ===== */}
        <Collapse id="sales-products" testId="sales-products" title="Products and price changes"
                  icon={<Package size={18} className="text-[#FF5A00]" />}
                  summary={data.products.erosion_total ? `${rs(data.products.erosion_total)} given up in price cuts` : "No price cuts"}>
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <div className="mb-2 text-sm font-semibold text-slate-900">Top products by revenue</div>
              {data.products.top.length === 0 ? <p className="text-xs text-slate-500">No invoice lines carry a product code in this period.</p> : data.products.top.map((p) => (
                <div key={p.code} className="flex items-baseline justify-between border-t border-slate-100 py-1.5 text-sm">
                  <span className="truncate pr-3 text-slate-800">{p.name}<span className="ml-2 font-mono-num text-xs text-slate-400">{p.code}</span></span>
                  <span className="shrink-0 font-mono-num font-semibold">{rs(p.revenue)} <span className={`text-xs ${p.change < 0 ? "text-red-700" : "text-emerald-700"}`}>{p.change > 0 ? "+" : ""}{rs(p.change)}</span></span>
                </div>
              ))}
            </div>
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900"><Percent size={15} className="text-[#FF5A00]" /> Price erosion on repeat products</div>
              {data.products.erosion.length === 0 ? <p className="text-xs text-slate-500">No product was invoiced at a lower average price than in the previous period.</p> : data.products.erosion.map((e) => (
                <div key={e.code} className="flex items-baseline justify-between border-t border-slate-100 py-1.5 text-sm">
                  <span className="truncate pr-3 text-slate-800">{e.name}<span className="ml-2 text-xs text-slate-400">₹ {e.was} to ₹ {e.now} each ({e.drop_pct}% lower)</span></span>
                  <span className="shrink-0 font-mono-num font-bold text-red-700">-{rs(e.impact)}</span>
                </div>
              ))}
            </div>
          </div>
        </Collapse>

        {/* ===== Data notes ===== */}
        {warnings.length > 0 && (
          <Collapse id="sales-notes" testId="sales-notes" title="Data notes"
                    icon={<Info size={18} className="text-slate-400" />} summary={`${warnings.length} things to know`}>
            <ul className="grid gap-2 text-sm text-slate-700">
              {warnings.map((w) => <li key={w} className="flex gap-2"><AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" />{w}</li>)}
            </ul>
          </Collapse>
        )}
      </div>
    </div>
  );
}
